import React, { useCallback, useEffect, useState } from 'react';
import { orderAPI, showApiError } from '../../services/api';
import { useSubscription } from '../../context/SubscriptionContext';
import './PaymentHistoryPage.css';

const PAGE_SIZE = 10;

const CATEGORY_LABEL_KO = {
  SUBSCRIPTION: '구독형',
  MACHINE: '장비',
  PACKAGE: '패키지',
};

const PAYMENT_STATUS_LABEL_KO = {
  READY: '결제 준비',
  PENDING: '결제 대기',
  PAY_PENDING: '결제 대기',
  VIRTUAL_ACCOUNT_ISSUED: '가상계좌 발급',
  PAID: '결제 완료',
  FAILED: '결제 실패',
  CANCELLED: '결제 취소',
  CANCELED: '결제 취소',
  PARTIAL_CANCELLED: '부분 취소',
  PARTIALLY_CANCELLED: '부분 취소',
  REFUNDED: '환불 완료',
  AWAITING_PAYMENT: '결제 대기',
  COMPLETED: '결제 완료',
};

const ORDER_SOURCE_LABEL_KO = {
  DIRECT: '바로 구매',
  CART: '장바구니',
  CHECKOUT_DIRECT: '바로 구매',
  CHECKOUT_CART: '장바구니',
};

const normalizeEnumKey = (value) => String(value).trim().toUpperCase().replace(/[\s-]+/g, '_');

const paymentStatusLabel = (status) => {
  if (!status) return '-';
  return PAYMENT_STATUS_LABEL_KO[normalizeEnumKey(status)] || status;
};

const orderSourceLabel = (source) => {
  if (!source) return '-';
  return ORDER_SOURCE_LABEL_KO[normalizeEnumKey(source)] || source;
};

const formatWon = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount.toLocaleString('ko-KR')}원`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ko-KR');
};

const categoryLabel = (category) => {
  if (!category) return '-';
  return CATEGORY_LABEL_KO[category] || category;
};

const isPaidOrder = (order) => {
  const status = normalizeEnumKey(order?.status);
  return status === 'PAID' || status === 'COMPLETED';
};

const PaymentHistoryPage = ({ user }) => {
  const isAdmin = user?.role === 'ADMIN';
  const { subscriptionMap } = useSubscription();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [buyerNameInput, setBuyerNameInput] = useState('');
  const [buyerNameQuery, setBuyerNameQuery] = useState('');
  const [openingReceiptOrderId, setOpeningReceiptOrderId] = useState(null);

  const loadOrders = useCallback(async (page) => {
    try {
      setLoading(true);
      const response = await orderAPI.getOrders({
        page,
        size: PAGE_SIZE,
        userId: isAdmin ? undefined : user?.id,
        buyerName: isAdmin ? buyerNameQuery || undefined : undefined,
      });
      setOrders(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      setCurrentPage(response.number ?? page);
    } catch (error) {
      showApiError(error, '결제 내역을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [buyerNameQuery, isAdmin, user?.id]);

  useEffect(() => {
    loadOrders(0);
  }, [loadOrders]);

  const handlePageChange = (page) => {
    if (page < 0 || page >= totalPages || page === currentPage || loading) {
      return;
    }
    loadOrders(page);
  };

  const handleBuyerNameSearch = (event) => {
    event.preventDefault();
    setBuyerNameQuery(buyerNameInput.trim());
  };

  const handleBuyerNameReset = () => {
    setBuyerNameInput('');
    setBuyerNameQuery('');
  };

  const handleReceiptClick = async (orderId) => {
    setOpeningReceiptOrderId(orderId);
    try {
      const response = await orderAPI.getReceiptUrl(orderId);
      if (!response?.receiptUrl) {
        window.alert('매출전표를 불러올 수 없습니다.');
        return;
      }
      window.open(response.receiptUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showApiError(error, '매출전표를 불러오지 못했습니다.');
    } finally {
      setOpeningReceiptOrderId(null);
    }
  };

  const getPowerLocationLabel = (powerLocationId) => {
    if (!powerLocationId) return '-';
    const subscription = subscriptionMap.get(Number(powerLocationId));
    return subscription?.powerLocationName || `발전소 #${powerLocationId}`;
  };

  const tableRowClassName = isAdmin
    ? 'payment-history-table-row payment-history-table-row--admin'
    : 'payment-history-table-row';

  return (
    <div className="payment-history-page">
      <div className="payment-history-header">
        <h2>결제 내역</h2>
        <p>
          {isAdmin
            ? '전체 주문 및 결제 기록을 확인할 수 있습니다.'
            : '주문 및 결제 기록을 확인할 수 있습니다.'}
        </p>
      </div>

      {isAdmin && (
        <form className="payment-history-filters" onSubmit={handleBuyerNameSearch}>
          <label className="payment-history-filter-label" htmlFor="buyer-name-search">
            구매자 이름
          </label>
          <input
            id="buyer-name-search"
            type="text"
            className="payment-history-filter-input"
            value={buyerNameInput}
            onChange={(event) => setBuyerNameInput(event.target.value)}
            placeholder="이름으로 검색"
          />
          <button type="submit" className="payment-history-filter-button">
            검색
          </button>
          <button
            type="button"
            className="payment-history-filter-button payment-history-filter-button-secondary"
            onClick={handleBuyerNameReset}
          >
            초기화
          </button>
        </form>
      )}

      {loading ? (
        <div className="payment-history-state">결제 내역을 불러오는 중…</div>
      ) : orders.length === 0 ? (
        <div className="payment-history-state">결제 내역이 없습니다.</div>
      ) : (
        <>
          <div className="payment-history-summary">
            총 <strong>{totalElements.toLocaleString('ko-KR')}</strong>건
          </div>

          <div className="payment-history-table-wrapper">
            <div className={`${tableRowClassName} payment-history-table-header`}>
              <div className="payment-history-table-cell">주문번호</div>
              {isAdmin && (
                <>
                  <div className="payment-history-table-cell">사용자 ID</div>
                  <div className="payment-history-table-cell">이름</div>
                </>
              )}
              <div className="payment-history-table-cell">상품 내역</div>
              <div className="payment-history-table-cell">발전소</div>
              <div className="payment-history-table-cell">상태</div>
              <div className="payment-history-table-cell">유형</div>
              <div className="payment-history-table-cell">결제 금액</div>
              <div className="payment-history-table-cell">주문 일시</div>
              <div className="payment-history-table-cell">결제 일시</div>
              <div className="payment-history-table-cell">매출전표</div>
            </div>

            {orders.map((order) => (
              <div key={order.orderId} className={tableRowClassName}>
                <div className="payment-history-table-cell">{order.orderId}</div>
                {isAdmin && (
                  <>
                    <div className="payment-history-table-cell">
                      {order.username || order.userId || '-'}
                    </div>
                    <div className="payment-history-table-cell">
                      {order.buyerName || '-'}
                    </div>
                  </>
                )}
                <div className="payment-history-table-cell payment-history-products">
                  {(order.items || []).length > 0 ? (
                    (order.items || []).map((item) => (
                      <div key={item.orderItemId} className="payment-history-product-line">
                        <span className="payment-history-product-name">{item.productName}</span>
                        <span className="payment-history-product-detail">
                          {categoryLabel(item.productCategory)} · {item.quantity}개 · {formatWon(item.lineTotal)}
                          {item.productBillingPeriod != null && ` · ${item.productBillingPeriod}개월`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span>-</span>
                  )}
                </div>
                <div className="payment-history-table-cell">
                  {getPowerLocationLabel(order.powerLocationId)}
                </div>
                <div className="payment-history-table-cell">
                  {order.status ? (
                    <span className="payment-history-status">{paymentStatusLabel(order.status)}</span>
                  ) : (
                    '-'
                  )}
                </div>
                <div className="payment-history-table-cell">
                  {order.source ? (
                    <span className="payment-history-source">{orderSourceLabel(order.source)}</span>
                  ) : (
                    '-'
                  )}
                </div>
                <div className="payment-history-table-cell payment-history-amount">
                  {formatWon(order.totalAmount)}
                </div>
                <div className="payment-history-table-cell">
                  {formatDateTime(order.createdAt)}
                </div>
                <div className="payment-history-table-cell">
                  {formatDateTime(order.paidAt)}
                </div>
                <div className="payment-history-table-cell payment-history-receipt-cell">
                  {isPaidOrder(order) ? (
                    <button
                      type="button"
                      className="payment-history-receipt-button"
                      onClick={() => handleReceiptClick(order.orderId)}
                      disabled={openingReceiptOrderId === order.orderId}
                    >
                      {openingReceiptOrderId === order.orderId ? '열기 중…' : '매출전표'}
                    </button>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="payment-history-pagination">
              <button
                type="button"
                className="payment-history-page-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 0 || loading}
              >
                이전
              </button>
              <span className="payment-history-page-info">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                type="button"
                className="payment-history-page-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage + 1 >= totalPages || loading}
              >
                다음
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentHistoryPage;

